import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Sparkles,
  StickyNote,
  ScanLine,
  FileText,
  Download,
  Loader2,
  ShieldCheck,
  CheckCircle2,
  Camera,
  Images,
  AlertTriangle,
  CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { StatusPill, PriorityPill } from "../../components/app/ui";
import {
  CATEGORY_LABELS,
  PHOTO_CATEGORIES,
  PhotoCompareSlider,
  isImageRecord,
  isProgressPhoto,
  recordDate,
  recordUrl,
  shortDate,
  sortPhotoRecords,
  stageLabel,
} from "./ProgressPhotoTools";

const DECISIONS = [
  { key: "continue_treatment", label: "Continue treatment" },
  { key: "request_new_photos", label: "Request new photos" },
  { key: "schedule_followup", label: "Schedule follow-up" },
  { key: "needs_attention", label: "Needs clinical attention" },
];

const decisionLabel = (key) => DECISIONS.find((d) => d.key === key)?.label || key;
const prettyCategory = (key) => (key || "other").replace(/_/g, " ");

function AnalysisList({ title, items, testId, tone = "slate" }) {
  const styles = {
    slate: "border-slate-100 bg-slate-50/60 text-slate-700",
    amber: "border-amber-200 bg-amber-50/70 text-amber-800",
    cyan: "border-cyan-200 bg-cyan-50/60 text-cyan-800",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${styles[tone]}`} data-testid={testId}>
      <span className="block text-[11px] font-semibold tracking-[0.14em] uppercase opacity-70 mb-2">{title}</span>
      {items?.length ? (
        <ul className="space-y-1.5 text-sm leading-relaxed">
          {items.map((item, index) => (
            <li key={`${testId}-${index}`} className="flex gap-2">
              <span className="mt-2 h-1 w-1 rounded-full bg-current shrink-0 opacity-60" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm opacity-60">None reported.</p>
      )}
    </div>
  );
}

export default function ReviewDrawer({ review, onClose, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [decision, setDecision] = useState("");
  const [doctorNotes, setDoctorNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState("");
  const [compareCategory, setCompareCategory] = useState("intraoral_photos");
  const [compareBeforeId, setCompareBeforeId] = useState("");
  const [compareAfterId, setCompareAfterId] = useState("");

  const patientId = review?.patient_id;

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${patientId}`);
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    setDetail(null);
    setPreview(null);
    setDecision("");
    setDoctorNotes("");
    setAnalysis(review?.analysis || null);
    setAnalysisError("");
    setAnalyzing(false);
    setCompareCategory("intraoral_photos");
    setCompareBeforeId("");
    setCompareAfterId("");
    load();
  }, [load, review?.review_id, review?.analysis]);

  const completeReview = async () => {
    setCompleting(true);
    try {
      await api.post(`/reviews/${review.review_id}/complete`, { decision, doctor_notes: doctorNotes });
      toast.success("Review saved and marked as reviewed.");
      onUpdated();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCompleting(false);
    }
  };

  const patient = detail?.patient || review?.patient;
  const records = useMemo(() => detail?.records || [], [detail]);
  const photoRecords = useMemo(() => sortPhotoRecords(records.filter(isProgressPhoto)), [records]);
  const hasPhotos = photoRecords.length > 0;
  const isPending = review?.status === "pending";
  const compareOptions = useMemo(
    () => photoRecords.filter((record) => record.category === compareCategory),
    [photoRecords, compareCategory]
  );
  const compareBefore = compareOptions.find((record) => record.record_id === compareBeforeId) || compareOptions[0] || null;
  const selectedAfter = compareOptions.find((record) => record.record_id === compareAfterId);
  const compareAfter = selectedAfter && selectedAfter.record_id !== compareBefore?.record_id
    ? selectedAfter
    : compareOptions.find((record) => record.record_id !== compareBefore?.record_id) || null;

  useEffect(() => {
    const available = PHOTO_CATEGORIES.find((category) => photoRecords.some((record) => record.category === category));
    if (!photoRecords.some((record) => record.category === compareCategory) && available) {
      setCompareCategory(available);
      return;
    }
    const options = photoRecords.filter((record) => record.category === compareCategory);
    setCompareBeforeId((current) => (options.some((record) => record.record_id === current) ? current : options[0]?.record_id || ""));
    setCompareAfterId((current) => {
      if (options.some((record) => record.record_id === current) && current !== compareBeforeId) return current;
      return (options.find((record) => record.record_id !== compareBeforeId) || options[0])?.record_id || "";
    });
  }, [photoRecords, compareCategory, compareBeforeId]);

  const runAnalysis = async () => {
    if (!review?.review_id || !hasPhotos) return;
    setAnalyzing(true);
    setAnalysisError("");
    try {
      const { data } = await api.post(`/reviews/${review.review_id}/analyze`);
      setAnalysis(data.analysis);
      toast.success("AI progress analysis completed.");
      onUpdated?.();
    } catch (e) {
      const message = formatApiErrorDetail(e.response?.data?.detail);
      setAnalysisError(message);
      toast.error(message);
    } finally {
      setAnalyzing(false);
    }
  };

  const analysisPriority = analysis?.priority || review?.priority;
  const analysisConfidence = analysis?.confidence ?? review?.confidence;

  return (
    <AnimatePresence>
      {review && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: 640 }}
            animate={{ x: 0 }}
            exit={{ x: 640 }}
            transition={{ type: "spring", stiffness: 300, damping: 34 }}
            data-testid="review-drawer"
            className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white border-l border-slate-200 shadow-[0_0_60px_rgba(15,23,42,0.15)] overflow-y-auto"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 bg-white/90 backdrop-blur-xl border-b border-slate-100">
              <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-slate-400">AI Review Workspace</p>
              <button
                type="button"
                data-testid="review-drawer-close"
                onClick={onClose}
                className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" strokeWidth={1.8} />
              </button>
            </div>

            <div className="p-6 space-y-7">
              <div className="flex items-center gap-4">
                <span className="w-14 h-14 rounded-full bg-gradient-to-br from-[#2563EB] to-[#6366F1] flex items-center justify-center text-lg font-bold text-white shrink-0">
                  {patient ? `${patient.first_name[0]}${patient.last_name[0]}` : "?"}
                </span>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display text-xl tracking-tight text-[#0F172A]" data-testid="review-patient-name">
                    {patient ? `${patient.first_name} ${patient.last_name}` : "Unknown patient"}
                  </h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {patient?.display_id} · {patient?.treatment_type} · Stage {patient?.treatment_stage} of {patient?.treatment_total_stages}
                  </p>
                </div>
                <StatusPill status={review.status} />
              </div>

              {isPending ? (
                <div className="rounded-2xl border border-[#6366F1]/20 bg-[#6366F1]/5 p-5" data-testid="review-pending-analysis">
                  <div className="flex items-center gap-2.5 mb-2">
                    <span className="w-8 h-8 rounded-lg bg-[#6366F1]/10 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-[#6366F1]" strokeWidth={1.8} />
                    </span>
                    <p className="font-display text-base tracking-tight text-[#0F172A]">Pending Clinician Review</p>
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    Run AI analysis against this patient's staged progress photos, then make the final clinical decision.
                  </p>
                </div>
              ) : (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="review-completed-banner">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" strokeWidth={1.8} />
                    <div>
                      <p className="text-sm font-semibold text-emerald-700">Reviewed</p>
                      <p className="text-xs text-emerald-600/80 mt-0.5">
                        {review.reviewed_at ? `Completed ${new Date(review.reviewed_at).toLocaleString()}` : "This review has been completed."}
                      </p>
                      {review.decision && (
                        <p className="text-xs text-emerald-700 mt-1.5" data-testid="review-decision-saved">
                          Decision: <span className="font-semibold">{decisionLabel(review.decision)}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <section>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB]">
                    <Sparkles className="w-3.5 h-3.5" strokeWidth={1.8} /> AI Analysis
                  </p>
                  <span className="text-[9px] font-bold tracking-[0.15em] uppercase text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2.5 py-1" data-testid="ai-advisory-badge">
                    Advisory only · Clinician approval required
                  </span>
                </div>

                {!hasPhotos && !loading ? (
                  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4" data-testid="ai-no-image">
                    <Camera className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={1.8} />
                    <p className="text-sm text-slate-500">No progress photos are available for AI analysis.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid="ai-analysis-controls">
                      <div className="min-w-[220px] flex-1">
                        <p className="text-sm font-semibold text-[#0F172A]">
                          {analysis ? "AI analysis saved for this review" : "Analyze staged progress photos"}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {photoRecords.length} progress photo{photoRecords.length === 1 ? "" : "s"} across {new Set(photoRecords.map((record) => record.treatment_stage || "unstaged")).size} stage{new Set(photoRecords.map((record) => record.treatment_stage || "unstaged")).size === 1 ? "" : "s"}
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid="run-ai-analysis"
                        onClick={runAnalysis}
                        disabled={analyzing || !hasPhotos}
                        className="rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] px-5 py-2.5 text-xs font-semibold text-white shadow-[0_8px_20px_rgba(37,99,235,0.25)] transition-shadow duration-300 hover:shadow-[0_12px_28px_rgba(99,102,241,0.35)] disabled:opacity-60 flex items-center gap-2"
                      >
                        {analyzing && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        {analyzing ? "Analyzing Photos…" : analysis ? "Refresh AI Analysis" : "Run AI Analysis"}
                      </button>
                    </div>

                    {analysisError && (
                      <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="ai-analysis-error">
                        <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                        {analysisError}
                      </div>
                    )}

                    {analyzing && (
                      <div className="rounded-2xl border border-[#2563EB]/15 bg-[#2563EB]/5 p-4" data-testid="ai-analysis-loading">
                        <div className="flex items-center gap-2 text-sm font-medium text-[#1D4ED8] mb-3">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Comparing staged photos and drafting advisory findings…
                        </div>
                        <div className="space-y-2">
                          <div className="h-3 rounded-full bg-white/80 animate-pulse" />
                          <div className="h-3 w-4/5 rounded-full bg-white/80 animate-pulse" />
                          <div className="h-3 w-3/5 rounded-full bg-white/80 animate-pulse" />
                        </div>
                      </div>
                    )}

                    {!analysis && !analyzing && (
                      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-4 py-4" data-testid="ai-not-run">
                        <p className="text-sm text-slate-500">AI analysis has not been run for this review yet. No clinical finding is generated until you request it.</p>
                      </div>
                    )}

                    {analysis && !analyzing && (
                      <div className="space-y-3" data-testid="ai-analysis-results">
                        {analysis.insufficient ? (
                          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700" data-testid="ai-insufficient">
                            {analysis.image_quality_limitations?.[0] || "Insufficient image data for meaningful progress comparison."}
                          </div>
                        ) : (
                          <>
                            <div className="rounded-2xl border border-[#2563EB]/15 bg-[#2563EB]/5 p-4" data-testid="ai-progress-summary">
                              <span className="block text-[10px] font-bold tracking-[0.18em] uppercase text-[#2563EB] mb-2">Progress summary</span>
                              <p className="text-sm leading-relaxed text-slate-700">{analysis.progress_summary || "No summary returned."}</p>
                            </div>

                            {analysis.stages_compared?.length > 0 && (
                              <div className="flex flex-wrap items-center gap-2" data-testid="ai-stages-compared">
                                <span className="text-[10px] font-bold tracking-[0.16em] uppercase text-slate-400">Stages compared</span>
                                {analysis.stages_compared.map((stage) => (
                                  <span key={stage} className="rounded-full border border-[#2563EB]/20 bg-[#2563EB]/5 px-2.5 py-1 text-[10px] font-semibold text-[#1D4ED8]">
                                    Stage {stage}
                                  </span>
                                ))}
                              </div>
                            )}

                            <div className="space-y-2" data-testid="ai-observed-changes">
                              {analysis.observed_changes?.length ? (
                                analysis.observed_changes.map((change, index) => (
                                  <div key={`${change.category}-${index}`} className="rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3" data-testid={`ai-observed-${index}`}>
                                    <span className="block text-[10px] font-bold tracking-[0.16em] uppercase text-slate-400 mb-1">{prettyCategory(change.category)}</span>
                                    <p className="text-sm text-slate-700 leading-relaxed">{change.observation}</p>
                                  </div>
                                ))
                              ) : (
                                <AnalysisList title="Observed changes" items={[]} testId="ai-observed-empty" />
                              )}
                            </div>

                            <AnalysisList title="Potential concerns" items={analysis.potential_concerns} testId="ai-potential-concerns" tone="amber" />
                            <AnalysisList title="Image quality limitations" items={analysis.image_quality_limitations} testId="ai-image-limitations" tone="cyan" />
                            <AnalysisList title="Recommended clinician review" items={analysis.recommended_review ? [analysis.recommended_review] : []} testId="ai-recommended-review" />
                          </>
                        )}

                        <div className="grid grid-cols-2 gap-2">
                          <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-2.5">
                            <span className="text-slate-400 block text-[11px] mb-1">Priority / severity</span>
                            {analysisPriority ? <PriorityPill priority={analysisPriority} /> : <span className="text-sm text-slate-500">—</span>}
                          </div>
                          <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-2.5" data-testid="ai-confidence">
                            <span className="text-slate-400 block text-[11px] mb-1">AI confidence</span>
                            {analysisConfidence ? (
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-1.5 rounded-full bg-slate-200/70 overflow-hidden">
                                  <div className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1]" style={{ width: `${analysisConfidence}%` }} />
                                </div>
                                <span className="text-sm font-semibold text-[#0F172A]">{analysisConfidence}%</span>
                              </div>
                            ) : (
                              <span className="text-sm text-slate-500">—</span>
                            )}
                          </div>
                        </div>

                        <p className="flex items-center gap-2 text-[11px] text-slate-400" data-testid="ai-analysis-meta">
                          <CalendarDays className="w-3.5 h-3.5 text-[#0891B2]" strokeWidth={1.8} />
                          {analysis.model ? `${analysis.model} · ` : ""}{analysis.analyzed_at ? `Analyzed ${new Date(analysis.analyzed_at).toLocaleString()}` : "Analysis timestamp unavailable"}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </section>

              {hasPhotos && (
                <section data-testid="photo-comparison">
                  <p className="flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB] mb-3">
                    <Images className="w-3.5 h-3.5" strokeWidth={1.8} /> Photo Comparison
                  </p>
                  <div className="rounded-2xl border border-slate-200/80 p-4">
                    <div className="flex flex-wrap gap-2 mb-4" data-testid="review-compare-categories">
                      {PHOTO_CATEGORIES.map((category) => {
                        const count = photoRecords.filter((record) => record.category === category).length;
                        return (
                          <button
                            key={category}
                            type="button"
                            data-testid={`review-compare-category-${category}`}
                            onClick={() => setCompareCategory(category)}
                            className={`rounded-full border px-3 py-1.5 text-[10px] font-semibold tracking-[0.1em] uppercase transition-colors duration-200 ${
                              compareCategory === category
                                ? "border-[#2563EB]/40 bg-[#2563EB]/5 text-[#1D4ED8]"
                                : "border-slate-200 text-slate-400 hover:text-slate-600"
                            }`}
                          >
                            {CATEGORY_LABELS[category]} · {count}
                          </button>
                        );
                      })}
                    </div>
                    {compareOptions.length >= 2 ? (
                      <>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <select
                            data-testid="review-compare-before"
                            className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20"
                            value={compareBefore?.record_id || ""}
                            onChange={(event) => setCompareBeforeId(event.target.value)}
                          >
                            {compareOptions.map((record) => (
                              <option
                                key={record.record_id}
                                value={record.record_id}
                                label={`Before · ${stageLabel(record)} · ${shortDate(recordDate(record))}`}
                              />
                            ))}
                          </select>
                          <select
                            data-testid="review-compare-after"
                            className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20"
                            value={compareAfter?.record_id || ""}
                            onChange={(event) => setCompareAfterId(event.target.value)}
                          >
                            {compareOptions.map((record) => (
                              <option
                                key={record.record_id}
                                value={record.record_id}
                                disabled={record.record_id === compareBefore?.record_id}
                                label={`After · ${stageLabel(record)} · ${shortDate(recordDate(record))}`}
                              />
                            ))}
                          </select>
                        </div>
                        <PhotoCompareSlider before={compareBefore} after={compareAfter} />
                      </>
                    ) : (
                      <p className="text-sm text-slate-400 italic" data-testid="review-compare-empty">
                        At least two photos in this category are needed for a progress comparison.
                      </p>
                    )}
                  </div>
                </section>
              )}

              <section>
                <p className="flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB] mb-3">
                  <ScanLine className="w-3.5 h-3.5" strokeWidth={1.8} /> Patient Records
                </p>
                {loading ? (
                  <div className="h-14 rounded-xl bg-slate-100 animate-pulse" />
                ) : records.length === 0 ? (
                  <p className="text-sm text-slate-400 italic" data-testid="review-records-empty">No records attached to this patient.</p>
                ) : (
                  <div className="space-y-2" data-testid="review-records-list">
                    {records.map((record) => (
                      <button
                        key={record.record_id}
                        type="button"
                        onClick={() => (isImageRecord(record) ? setPreview(record) : window.open(recordUrl(record), "_blank"))}
                        data-testid={`review-record-${record.record_id}`}
                        className="group w-full flex items-center gap-3 rounded-xl border border-slate-200/80 px-4 py-3 text-left transition-[border-color,box-shadow] duration-200 hover:border-[#2563EB]/30 hover:shadow-[0_6px_18px_rgba(37,99,235,0.08)]"
                      >
                        <span className="w-10 h-10 rounded-lg bg-[#2563EB]/10 flex items-center justify-center shrink-0 overflow-hidden">
                          {isImageRecord(record) ? (
                            <img src={recordUrl(record)} alt="" loading="lazy" className="w-full h-full object-cover" />
                          ) : (
                            <FileText className="w-4 h-4 text-[#2563EB]" strokeWidth={1.7} />
                          )}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#0F172A] truncate">{record.file_name}</p>
                          <p className="text-[11px] text-slate-400">
                            {CATEGORY_LABELS[record.category] || record.category} · {stageLabel(record)} · {shortDate(recordDate(record))}
                          </p>
                          {record.description && <p className="text-[11px] text-slate-500 truncate mt-0.5">{record.description}</p>}
                        </div>
                        <Download className="w-4 h-4 text-slate-300 group-hover:text-[#2563EB] transition-colors shrink-0" strokeWidth={1.8} />
                      </button>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <p className="flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB] mb-3">
                  <StickyNote className="w-3.5 h-3.5" strokeWidth={1.8} /> Clinical Review
                </p>
                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                    <span className="text-slate-400 block text-[11px] mb-1">Patient clinical notes</span>
                    {patient?.clinical_notes ? (
                      <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="review-clinical-notes">{patient.clinical_notes}</p>
                    ) : (
                      <p className="text-sm text-slate-400 italic">No clinical notes recorded.</p>
                    )}
                  </div>

                  {isPending ? (
                    <>
                      <div data-testid="review-decision-options">
                        <span className="text-slate-400 block text-[11px] mb-2">Your decision</span>
                        <div className="grid grid-cols-2 gap-2">
                          {DECISIONS.map((item) => (
                            <button
                              key={item.key}
                              type="button"
                              data-testid={`decision-${item.key}`}
                              onClick={() => setDecision(item.key)}
                              className={`rounded-xl border px-3.5 py-3 text-xs font-semibold text-left transition-[border-color,background-color,color] duration-200 ${
                                decision === item.key
                                  ? "border-[#2563EB] bg-[#2563EB]/5 text-[#1D4ED8]"
                                  : "border-slate-200 text-slate-500 hover:border-[#2563EB]/40 hover:text-slate-700"
                              }`}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[11px] mb-2">Orthodontist notes / action</span>
                        <textarea
                          data-testid="review-doctor-notes"
                          rows={3}
                          value={doctorNotes}
                          onChange={(event) => setDoctorNotes(event.target.value)}
                          placeholder="Your assessment and instructions…"
                          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder-slate-400 outline-none resize-none transition-[border-color,box-shadow] duration-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20"
                        />
                      </div>
                    </>
                  ) : (
                    review.doctor_notes && (
                      <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4" data-testid="review-doctor-notes-readonly">
                        <span className="text-slate-400 block text-[11px] mb-1">Orthodontist notes</span>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{review.doctor_notes}</p>
                      </div>
                    )
                  )}
                </div>
              </section>

              <p className="flex items-center gap-2 text-[11px] text-slate-400 pt-1 border-t border-slate-100">
                <ShieldCheck className="w-3.5 h-3.5 text-[#0891B2] mt-3 shrink-0" strokeWidth={1.8} />
                <span className="mt-3">AI output is advisory only. The clinician remains responsible for all clinical decisions.</span>
              </p>

              {isPending && (
                <button
                  type="button"
                  data-testid="review-mark-reviewed"
                  onClick={completeReview}
                  disabled={completing}
                  className="w-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] py-3.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(37,99,235,0.25)] hover:shadow-[0_14px_30px_rgba(99,102,241,0.35)] transition-shadow duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {completing && <Loader2 className="w-4 h-4 animate-spin" />}
                  Mark as Reviewed
                </button>
              )}
            </div>
          </motion.aside>

          <AnimatePresence>
            {preview && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setPreview(null)}
                data-testid="record-lightbox"
                className="fixed inset-0 z-[60] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-6"
              >
                <div className="max-w-3xl w-full" onClick={(event) => event.stopPropagation()}>
                  <motion.img
                    initial={{ scale: 0.94, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    src={recordUrl(preview)}
                    alt={preview.file_name}
                    className="w-full max-h-[75vh] object-contain rounded-2xl bg-white"
                  />
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-white/80">
                      {preview.file_name}
                      <span className="text-white/40"> · {CATEGORY_LABELS[preview.category] || preview.category} · {stageLabel(preview)} · {shortDate(recordDate(preview))}</span>
                    </p>
                    <div className="flex gap-2">
                      <a
                        href={recordUrl(preview)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="lightbox-download"
                        className="rounded-full bg-white/10 border border-white/20 px-4 py-2 text-xs font-semibold text-white hover:bg-white/20 transition-colors"
                      >
                        Download
                      </a>
                      <button
                        type="button"
                        data-testid="lightbox-close"
                        onClick={() => setPreview(null)}
                        className="rounded-full bg-white px-4 py-2 text-xs font-semibold text-slate-900 hover:bg-slate-100 transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}
